// src/store/useStore.ts
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  orderBy,
  where,
  setDoc,
  serverTimestamp,
  arrayUnion,
  getDocs,
  getDoc,
} from 'firebase/firestore';
import { createWithEqualityFn } from 'zustand/traditional';
import { shallow } from 'zustand/shallow';
import { db, auth } from '../config/firebase';
import { Task, Project, KanbanBoard, CollaboratorDocument, UserProjectState } from '../types';
import { User } from 'firebase/auth';
import { onAuthStateChanged } from 'firebase/auth';
import { NotificationService } from '../services/notificationService';
import { Timestamp } from 'firebase/firestore';

interface Store {
  user: User | null;
  setUser: (user: User | null) => Promise<void>;
  updateUser: (user: User) => Promise<void>;
  tasks: Task[];
  allTasks: Task[]; // Including archived tasks
  boardTasks: Task[];
  addTask: (task: Partial<Task>) => Promise<string>;
  updateTask: (taskId: string, updates: Partial<Task>) => Promise<void>;
  deleteTask: (taskId: string) => Promise<void>;
  fetchTasks: () => void;
  fetchTasksBoard: (projectId: string) => (() => void) | undefined;
  initialized: boolean;
  projects: Project[];
  addProject: (project: Omit<Project, 'id' | 'createdAt' | 'userId' | 'boards' | 'collaborators' | 'ownerId'>) => Promise<string>;
  updateProject: (id: string, updates: Partial<Project>) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;
  addBoard: (projectId: string, board: Omit<KanbanBoard, 'id' | 'createdAt' | 'projectId'>) => Promise<string>;
  updateBoard: (projectId: string, boardId: string, updates: Partial<KanbanBoard>) => Promise<void>;
  deleteBoard: (projectId: string, boardId: string) => Promise<void>;
  fetchProjects: () => (() => void) | undefined;
  reorderTasks: (tasks: Task[], activeTaskId: string, overTaskId: string, destinationBoardId: string) => Promise<void>;
  reorderTasksOptimistically: (updatedTasks: Task[]) => void;
  collaborations: CollaboratorDocument[];
  loading: boolean;
  error: string | null;
  userProjectStates: UserProjectState[];
  archiveProjectForUser: (projectId: string) => Promise<void>;
  deleteProjectForUser: (projectId: string) => Promise<void>;
  fetchUserProjectStates: () => void;
}

export const useStore = createWithEqualityFn<Store>((set, get) => ({
  user: null,
  initialized: false,
  tasks: [],
  allTasks: [], // Including archived tasks
  boardTasks: [],
  collaborations: [],
  loading: false,
  error: null,
  userProjectStates: [],

  setUser: async (user: User | null) => {
    try {
      set({ user });
      
      if (user) {
        // Store or update user data in Firestore
        const userRef = doc(db, 'users', user.uid);
        await setDoc(
          userRef,
          {
            email: user.email,
            displayName: user.displayName,
            photoURL: user.photoURL,
            lastLogin: serverTimestamp(),
          },
          { merge: true }
        );

        // Fetch tasks, projects, and user project states
        get().fetchTasks();
        get().fetchProjects();
        get().fetchUserProjectStates();

        // Check for pending invitations
        if (user.email) {
          await NotificationService.checkPendingInvitations(user.email);
        }
      } else {
        // Clear all data when user is null
        set({ tasks: [], projects: [], userProjectStates: [] });
      }
    } catch (error) {
      console.error('Error setting user:', error);
    }
  },

  updateUser: async (user: User) => {
    try {
      const userRef = doc(db, 'users', user.uid);
      await setDoc(
        userRef,
        {
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      set({ user });
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  },

  addTask: async (task: Partial<Task>) => {
    try {
      set({ loading: true, error: null });
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const taskRef = collection(db, 'tasks');
      const docRef = await addDoc(taskRef, {
        ...task,
        userId: user.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        order: (get().tasks.length + 1) * 1000,
        scheduledTime: task.scheduledTime ? Timestamp.fromDate(task.scheduledTime as Date) : null,
        deadline: task.deadline ? Timestamp.fromDate(task.deadline as Date) : null,
        startedAt: task.startedAt ? Timestamp.fromDate(task.startedAt as Date) : null,
        completedAt: task.completedAt ? Timestamp.fromDate(task.completedAt as Date) : null,
        archivedAt: task.archivedAt ? Timestamp.fromDate(task.archivedAt as Date) : null
      });
      return docRef.id;
    } catch (error) {
      console.error('Error adding task:', error);
      set({ error: 'Failed to add task' });
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  updateTask: async (taskId: string, updates: Partial<Task>) => {
    try {
      set({ loading: true, error: null });
      const taskRef = doc(db, 'tasks', taskId);
      
      // Convert Date objects to Firestore Timestamps for storage
      const processedUpdates: Record<string, any> = { ...updates };
      if (updates.scheduledTime instanceof Date) {
        processedUpdates.scheduledTime = Timestamp.fromDate(updates.scheduledTime);
      }
      if (updates.deadline instanceof Date) {
        processedUpdates.deadline = Timestamp.fromDate(updates.deadline);
      }
      if (updates.startedAt instanceof Date) {
        processedUpdates.startedAt = Timestamp.fromDate(updates.startedAt);
      }
      if (updates.completedAt instanceof Date) {
        processedUpdates.completedAt = Timestamp.fromDate(updates.completedAt);
      }
      if (updates.archivedAt instanceof Date) {
        processedUpdates.archivedAt = Timestamp.fromDate(updates.archivedAt);
      }
      
      await updateDoc(taskRef, processedUpdates);
    } catch (error) {
      console.error('Error updating task:', error);
      set({ error: 'Failed to update task' });
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  deleteTask: async (taskId: string) => {
    const { user } = get();
    if (!user) throw new Error('User must be logged in to delete tasks');

    try {
      // Optimistically update UI state
      const currentTasks = get().tasks;
      const currentBoardTasks = get().boardTasks;
      const currentAllTasks = get().allTasks;

      // Remove task from all task lists
      set({
        tasks: currentTasks.filter(task => task.id !== taskId),
        boardTasks: currentBoardTasks.filter(task => task.id !== taskId),
        allTasks: currentAllTasks.filter(task => task.id !== taskId)
      });

      // Delete from Firestore
      const taskRef = doc(db, 'tasks', taskId);
      await deleteDoc(taskRef);
    } catch (error) {
      console.error('Error deleting task:', error);
      throw error;
    }
  },

  fetchTasks: () => {
    const user = auth.currentUser;
    if (!user) return;

    // Create compound query for both user's tasks and assigned tasks
    const userTasksQuery = query(
      collection(db, 'tasks'),
      where('userId', '==', user.uid),
      orderBy('order', 'asc')
    );

    const assignedTasksQuery = query(
      collection(db, 'tasks'),
      where('assigneeId', '==', user.uid),
      orderBy('order', 'asc')
    );

    // Subscribe to both queries
    const unsubscribeUserTasks = onSnapshot(userTasksQuery, (userSnapshot) => {
      const unsubscribeAssignedTasks = onSnapshot(assignedTasksQuery, (assignedSnapshot) => {
        const fetchedTasks = new Map<string, Task>();

        // Process user's tasks
        userSnapshot.forEach((doc) => {
          const taskData = doc.data();
          fetchedTasks.set(doc.id, {
            id: doc.id,
            title: taskData.title,
            description: taskData.description,
            priority: taskData.priority,
            status: taskData.status,
            userId: taskData.userId,
            order: taskData.order,
            createdAt: taskData.createdAt?.toDate() || new Date(),
            updatedAt: taskData.updatedAt?.toDate() || taskData.createdAt?.toDate() || new Date(),
            projectId: taskData.projectId || undefined,
            boardId: taskData.boardId || undefined,
            estimatedTime: taskData.estimatedTime || undefined,
            scheduledTime: taskData.scheduledTime?.toDate() || null,
            deadline: taskData.deadline?.toDate() || null,
            startedAt: taskData.startedAt?.toDate() || null,
            completedAt: taskData.completedAt?.toDate() || null,
            archivedAt: taskData.archivedAt?.toDate() || null,
            assigneeId: taskData.assigneeId || undefined,
            assigneeEmail: taskData.assigneeEmail || undefined
          });
        });

        // Process assigned tasks
        assignedSnapshot.forEach((doc) => {
          if (!fetchedTasks.has(doc.id)) {
            const taskData = doc.data();
            fetchedTasks.set(doc.id, {
              id: doc.id,
              title: taskData.title,
              description: taskData.description,
              priority: taskData.priority,
              status: taskData.status,
              userId: taskData.userId,
              order: taskData.order,
              createdAt: taskData.createdAt?.toDate() || new Date(),
              updatedAt: taskData.updatedAt?.toDate() || taskData.createdAt?.toDate() || new Date(),
              projectId: taskData.projectId || undefined,
              boardId: taskData.boardId || undefined,
              estimatedTime: taskData.estimatedTime || undefined,
              scheduledTime: taskData.scheduledTime?.toDate() || null,
              deadline: taskData.deadline?.toDate() || null,
              startedAt: taskData.startedAt?.toDate() || null,
              completedAt: taskData.completedAt?.toDate() || null,
              archivedAt: taskData.archivedAt?.toDate() || null,
              assigneeId: taskData.assigneeId || undefined,
              assigneeEmail: taskData.assigneeEmail || undefined
            });
          }
        });

        const allTasks = Array.from(fetchedTasks.values());
        
        // Set all tasks
        set({ allTasks });
        
        // Filter out archived tasks for active views
        const activeTasks = allTasks.filter(task => !task.archivedAt);
        set({ tasks: activeTasks });
      });

      return () => {
        unsubscribeAssignedTasks();
      };
    });

    return () => {
      unsubscribeUserTasks();
    };
  },

  projects: [],

  addProject: async (project) => {
    const { user } = get();
    if (!user) throw new Error('User must be logged in to add projects');

    try {
      // First create the project document to get its ID
      const projectRef = await addDoc(collection(db, 'projects'), {
        ...project,
        userId: user.uid,
        ownerId: user.uid,
        createdAt: new Date(),
        collaborators: [],
      });

      // Create a unique default board ID using the project ID
      const defaultBoardId = `${projectRef.id}_default`;
      
      // Update the project with the default board
      await updateDoc(projectRef, {
        boards: [{
          id: defaultBoardId,
          title: 'To Do',
          order: 0,
          createdAt: new Date(),
          projectId: projectRef.id
        }]
      });

      return projectRef.id;
    } catch (error) {
      console.error('Error adding project:', error);
      throw error;
    }
  },
  reorderTasks: async (tasks, activeTaskId, overTaskId, destinationBoardId) => {
    const { updateTask } = get();
    const activeTask = tasks.find(task => task.id === activeTaskId);
    if (!activeTask) return;

    const destinationTasks = tasks
      .filter(task => task.boardId === destinationBoardId)
      .sort((a, b) => a.order - b.order);

    const overTaskIndex = destinationTasks.findIndex(task => task.id === overTaskId);
    const newOrder = overTaskIndex === -1 ? destinationTasks.length : overTaskIndex;

    // Update the active task's boardId and order
    await updateTask(activeTaskId, { boardId: destinationBoardId, order: newOrder });

    // Optionally, update the order of other tasks in the destination board
    const updates = destinationTasks.map((task, index) => {
      if (task.id !== activeTaskId && index >= newOrder) {
        return updateTask(task.id, { order: index + 1 });
      }
      return Promise.resolve();
    });

    await Promise.all(updates);
  },
  updateProject: async (id, updates) => {
    const { user } = get();
    if (!user) throw new Error('User must be logged in to update projects');

    try {
      const projectRef = doc(db, 'projects', id);
      const processedUpdates = { ...updates };
      
      // Convert Date objects to Firestore Timestamps
      if (updates.archivedAt instanceof Date) {
        processedUpdates.archivedAt = Timestamp.fromDate(updates.archivedAt);
      }
      
      await updateDoc(projectRef, processedUpdates);
    } catch (error) {
      console.error('Error updating project:', error);
      throw error;
    }
  },

  deleteProject: async (id: string) => {
    const { user } = get();
    if (!user) throw new Error('User must be logged in to delete projects');

    try {
      // Optimistically update UI state
      const currentProjects = get().projects;
      set({ projects: currentProjects.filter(project => project.id !== id) });

      // Delete from Firestore
      const projectRef = doc(db, 'projects', id);
      await deleteDoc(projectRef);
    } catch (error) {
      // Revert optimistic update on error
      const currentProjects = get().projects;
      set({ projects: currentProjects });
      console.error('Error deleting project:', error);
      throw error;
    }
  },

  addBoard: async (projectId, board) => {
    const { user } = get();
    if (!user) throw new Error('User must be logged in to add boards');

    try {
      const projectRef = doc(db, 'projects', projectId);
      const now = new Date();
      const newBoardId = crypto.randomUUID(); // Generate a unique ID

      const boardToAdd = {
        ...board,
        id: newBoardId,
        projectId,
        createdAt: now
      };

      await updateDoc(projectRef, {
        boards: arrayUnion(boardToAdd)
      });

      return newBoardId; // Return the generated boardId
    } catch (error) {
      console.error('Error adding board:', error);
      throw error;
    }
  },

  updateBoard: async (projectId, boardId, updates) => {
    const { user, projects } = get();
    if (!user) throw new Error('User must be logged in to update boards');

    try {
      const project = projects.find(p => p.id === projectId);
      if (!project) throw new Error('Project not found');

      const updatedBoards = project.boards.map(board => 
        board.id === boardId ? { ...board, ...updates } : board
      );

      const projectRef = doc(db, 'projects', projectId);
      await updateDoc(projectRef, { boards: updatedBoards });
    } catch (error) {
      console.error('Error updating board:', error);
      throw error;
    }
  },

  deleteBoard: async (projectId, boardId) => {
    const { user, projects } = get();
    if (!user) throw new Error('User must be logged in to delete boards');

    try {
      // Optimistically update UI state
      const project = projects.find(p => p.id === projectId);
      if (!project) throw new Error('Project not found');

      const updatedBoards = project.boards.filter(board => board.id !== boardId);
      const updatedProjects = projects.map(p => 
        p.id === projectId ? { ...p, boards: updatedBoards } : p
      );
      set({ projects: updatedProjects });

      // Update Firestore
      const projectRef = doc(db, 'projects', projectId);
      await updateDoc(projectRef, { boards: updatedBoards });
    } catch (error) {
      console.error('Error deleting board:', error);
      throw error;
    }
  },
  /**
   * Optimistically reorder tasks in the local state.
   * @param updatedTasks - The new order of tasks.
   */
  reorderTasksOptimistically: (updatedTasks: Task[]) => {
    set((state) => ({
      tasks: state.tasks.map(task => {
        const updatedTask = updatedTasks.find(t => t.id === task.id);
        return updatedTask ? { ...task, order: updatedTask.order } : task;
      }),
    }));
  },
  fetchProjects: () => {
    const { user } = get();
    if (!user) return undefined;

    try {

      // Query for projects owned by user
      const ownedQuery = query(
        collection(db, 'projects'),
        where('ownerId', '==', user.uid)
      );

      // Query for collaborations where user is a collaborator
      const collaborationsQuery = query(
        collection(db, 'collaborators'),
        where('userId', '==', user.uid),
        where('status', '==', 'accepted')
      );

      // Set up real-time listeners for both queries
      const unsubscribeOwned = onSnapshot(
        ownedQuery,
        () => {
          
          processProjectUpdates();
        },
        (error) => {
          console.error('Error in owned projects listener:', error);
        }
      );

      const unsubscribeCollaborations = onSnapshot(
        collaborationsQuery,
        () => {
          
          processProjectUpdates();
        },
        (error) => {
          console.error('Error in collaborations listener:', error);
        }
      );

      // Function to process updates from either listener
      async function processProjectUpdates() {
        try {
          // Get current snapshots
          const [ownedSnapshot, collaborationsSnapshot] = await Promise.all([
            getDocs(ownedQuery),
            getDocs(collaborationsQuery)
          ]);

        

          // Get all project IDs from collaborations
          const collaboratedProjectIds = collaborationsSnapshot.docs.map(doc => doc.data().projectId);
          
          // Fetch all collaborated projects
          const collaboratedProjects = collaboratedProjectIds.length > 0 ? await Promise.all(
            collaboratedProjectIds.map(async (projectId) => {
              const projectDoc = await getDoc(doc(db, 'projects', projectId));
              if (!projectDoc.exists()) return null;
              const data = projectDoc.data();
              return { 
                id: projectDoc.id,
                title: data.title,
                description: data.description,
                createdAt: data.createdAt,
                userId: data.userId,
                boards: data.boards || [],
                ownerId: data.ownerId
              } as Project;
            })
          ) : [];

          // Combine projects and remove duplicates
          const projects = new Map();
          
          // Add owned projects
          ownedSnapshot.forEach(doc => {
            const data = doc.data();
            
            projects.set(doc.id, { 
              id: doc.id,
              title: data.title,
              description: data.description,
              createdAt: data.createdAt?.toDate(),
              userId: data.userId,
              boards: data.boards?.map((board: any) => ({
                ...board,
                createdAt: board.createdAt?.toDate()
              })) || [],
              ownerId: data.ownerId,
              archivedAt: data.archivedAt?.toDate() || null
            } as Project);
          });

          // Add collaborated projects
          collaboratedProjects.forEach(project => {
            if (project && !projects.has(project.id)) {
            
              const createdAt = project.createdAt instanceof Timestamp ? 
                project.createdAt.toDate() : 
                project.createdAt instanceof Date ? 
                  project.createdAt : 
                  new Date();

              const boards = project.boards?.map((board: any) => ({
                ...board,
                createdAt: board.createdAt instanceof Timestamp ? 
                  board.createdAt.toDate() : 
                  board.createdAt instanceof Date ? 
                    board.createdAt : 
                    new Date()
              })) || [];

              const archivedAt = project.archivedAt instanceof Timestamp ?
                project.archivedAt.toDate() :
                project.archivedAt instanceof Date ?
                  project.archivedAt :
                  null;
              
              projects.set(project.id, { 
                ...project,
                createdAt,
                boards,
                archivedAt
              } as Project);
            }
          });

          const uniqueProjects = Array.from(projects.values());
         

          set({ projects: uniqueProjects });
        } catch (error) {
          console.error('Error processing project updates:', error);
        }
      }

      // Return cleanup function
      return () => {
        unsubscribeOwned();
        unsubscribeCollaborations();
      };
    } catch (error) {
      console.error('Error setting up project listeners:', error);
      return undefined;
    }
  },

  fetchTasksBoard: (projectId) => {
    const { user } = get();
    if (!user) return;

    const tasksRef = collection(db, 'tasks');
    // Query tasks for this project only
    const q = query(
      tasksRef,
      where('projectId', '==', projectId),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      {
        next: (snapshot) => {
          const tasks = snapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              title: data.title,
              description: data.description,
              priority: data.priority,
              status: data.status,
              userId: data.userId,
              order: data.order || 0,
              projectId: data.projectId,
              boardId: data.boardId || 'default',
              estimatedTime: data.estimatedTime,
              assigneeId: data.assigneeId || undefined,
              assigneeEmail: data.assigneeEmail || undefined,
              createdAt: data.createdAt?.toDate() || new Date(),
              scheduledTime: data.scheduledTime?.toDate(),
              deadline: data.deadline?.toDate(),
              startedAt: data.startedAt?.toDate(),
              completedAt: data.completedAt?.toDate(),
              archivedAt: data.archivedAt?.toDate()
            } as Task;
          });

          // Filter out archived tasks for active board views
          const activeTasks = tasks.filter(task => !task.archivedAt);

          // Sort tasks by order and creation date
          activeTasks.sort((a, b) => {
            const orderDiff = (a.order || 0) - (b.order || 0);
            if (orderDiff !== 0) return orderDiff;
            return (a.createdAt?.getTime() || 0) - (b.createdAt?.getTime() || 0);
          });

          set({ boardTasks: activeTasks });
        },
        error: (error) => {
          console.error('Error fetching board tasks:', error);
          if (error.code === 'failed-precondition') {
            console.warn('Index not ready for board tasks query');
          }
        },
      }
    );

    return unsubscribe;
  },

  fetchUserProjectStates: () => {
    const user = auth.currentUser;
    if (!user) return;

    const userProjectStatesRef = collection(db, 'user_project_states');
    const q = query(userProjectStatesRef, where('userId', '==', user.uid));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const states = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
      })) as UserProjectState[];
      set({ userProjectStates: states });
    });

    return unsubscribe;
  },

  archiveProjectForUser: async (projectId: string) => {
    const user = auth.currentUser;
    if (!user) throw new Error('User must be logged in to archive projects');

    const userProjectStatesRef = collection(db, 'user_project_states');
    const q = query(
      userProjectStatesRef,
      where('userId', '==', user.uid),
      where('projectId', '==', projectId)
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      // Create new state
      await addDoc(userProjectStatesRef, {
        userId: user.uid,
        projectId,
        isArchived: true,
        isDeleted: false,
        updatedAt: serverTimestamp(),
      });
    } else {
      // Toggle archive state
      const docRef = doc(db, 'user_project_states', snapshot.docs[0].id);
      const currentState = snapshot.docs[0].data();
      await updateDoc(docRef, {
        isArchived: !currentState.isArchived,
        updatedAt: serverTimestamp(),
      });
    }
  },

  deleteProjectForUser: async (projectId: string) => {
    const user = auth.currentUser;
    if (!user) throw new Error('User must be logged in to delete projects');

    const userProjectStatesRef = collection(db, 'user_project_states');
    const q = query(
      userProjectStatesRef,
      where('userId', '==', user.uid),
      where('projectId', '==', projectId)
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      // Create new state
      await addDoc(userProjectStatesRef, {
        userId: user.uid,
        projectId,
        isArchived: false,
        isDeleted: true,
        updatedAt: serverTimestamp(),
      });
    } else {
      // Update existing state
      const docRef = doc(db, 'user_project_states', snapshot.docs[0].id);
      await updateDoc(docRef, {
        isDeleted: true,
        updatedAt: serverTimestamp(),
      });
    }
  },
}), shallow);

// Initialize auth state listener
let unsubscribeAuth: (() => void) | undefined;

onAuthStateChanged(auth, async (user) => {
  if (!unsubscribeAuth) {
    const store = useStore.getState();
    try {
      await store.setUser(user);
    } catch (error) {
      console.error('Error setting user:', error);
    } finally {
      if (!store.initialized) {
        useStore.setState({ initialized: true });
      }
    }
    unsubscribeAuth = () => auth.onAuthStateChanged(() => {});
  }
});
